/** Placeholder rendered for any missing value. */
export const DASH = "—";

const numberFormat = new Intl.NumberFormat("es-CO", {
  maximumFractionDigits: 0,
});
const decimalFormat = new Intl.NumberFormat("es-CO", {
  maximumFractionDigits: 1,
});

export const isNum = (n: unknown): n is number =>
  typeof n === "number" && Number.isFinite(n);

export const fmtNumber = (n?: number | null): string =>
  isNum(n) ? numberFormat.format(n) : DASH;

export const fmtDecimal = (n?: number | null): string =>
  isNum(n) ? decimalFormat.format(n) : DASH;

export const fmtMoney = (n?: number | null): string =>
  isNum(n) ? `$ ${numberFormat.format(n)}` : DASH;

/** n is already a percentage (0–100). */
export const fmtPercent = (n?: number | null): string =>
  isNum(n) ? `${decimalFormat.format(n)}%` : DASH;

export const fmtText = (s?: string | number | boolean | null): string => {
  if (s === null || s === undefined) return DASH;
  const str = String(s).trim();
  return str === "" ? DASH : str;
};

export const fmtYesNo = (b?: boolean | null): string =>
  b === true ? "SÍ" : b === false ? "NO" : DASH;

/** Groups all-digit document numbers (1098765432 → 1.098.765.432); leaves anything else as-is. */
export const fmtDocNumber = (s?: string | null): string => {
  const str = fmtText(s);
  return /^\d+$/.test(str) ? numberFormat.format(Number(str)) : str;
};

const pad = (n: number) => String(n).padStart(2, "0");

/** ISO/Date-ish string → "YYYY-MM-DD HH:mm:ss" (local time); unparseable strings are shown as-is. */
export const fmtDateTime = (s?: string | null): string => {
  const str = fmtText(s);
  if (str === DASH) return DASH;
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return str;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const MONTHS = [
  "ENE",
  "FEB",
  "MAR",
  "ABR",
  "MAY",
  "JUN",
  "JUL",
  "AGO",
  "SEP",
  "OCT",
  "NOV",
  "DIC",
];

/** "2026-06-30" / "2026-06" / "2026/06/30" → "JUN 26"; anything else is shown as-is. */
export const fmtMonthYear = (s?: string | null): string => {
  const str = fmtText(s);
  const m = /^(\d{4})[-/](\d{1,2})/.exec(str);
  if (!m) return str;
  const month = Number(m[2]);
  if (month < 1 || month > 12) return str;
  return `${MONTHS[month - 1]} ${m[1].slice(2)}`;
};

/** Same as fmtMonthYear but only the month ("OCT"), for compact column headers. */
export const fmtMonth = (s?: string | null): string => {
  const full = fmtMonthYear(s);
  return /^[A-Z]{3} \d{2}$/.test(full) ? full.slice(0, 3) : full;
};
